import { QueryGroup } from "./../../shared/cubiq-model-multi-asset/model/QueryGroup";
// Angular + external
import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from "@angular/core";
import { LoggerService } from "@bfx/logger";
import { AutoComplete } from "@bfx/primeng/autocomplete";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subject } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { AssetClass } from "shared/cubiq-model-multi-asset/model/enum/AssetClass";
import { BlotterType } from "shared/cubiq-model-multi-asset/model/enum/BlotterType";
import {
  BfxBlotterFilter,
  BfxBlotterFilterActionEvent,
  BfxBlotterFilterCondition,
  EXTENDED_TYPE_PROPERTY_NAME,
  ExtendedOrderTypeIdentifier,
  FilterType,
} from "../bfx-blotter-filter/bfx-blotter-filter.constants";
import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";
import { BFXBLOTTERFILTERTYPES } from "../bfx-blotter-filter/models/bfx-blotter-filter.constants";
import { BfxDialogBaseComponent } from "./bfx-dialog-base.component";
import { BfxDialogData } from "./models/bfx-dialog-data.model";
const logger = LoggerService.for(__dirname);

import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";

const CONFLICTING_ORDER_DATA_FIELDS_FOR_FILTER_CONDITIONS = [
  "fixingSourceCd",
  "requestedOrderType",
  "deskId",
  "traderGroupNames",
];

@Component({
  selector: "bfx-dialog-filter-copy",
  templateUrl: "./html/bfx-dialog-filter-copy.html",
  standalone: false,
})
export class BfxDialogFilterCopyComponent
  extends BfxDialogBaseComponent
  implements AfterViewInit, OnDestroy
{
  @ViewChild("copyFilter_filterName_autoComplete", { static: true })
  private _copyFilter_filterName_autoComplete: AutoComplete;

  @ViewChild("copyFilter_filterType_autoComplete", { static: true })
  private _copyFilter_filterType_autoComplete: AutoComplete;

  public filterNameValid: boolean = false;
  public filterNameEmpty: boolean = true;
  public filterTypeValid: boolean = false;
  public filterTypeInvalid: boolean = false;
  public filterTypePartValid: boolean = false;
  private filterConditions: Array<BfxBlotterFilterCondition> = [];
  private filterQueryGroupRules: Array<CubiqTS.QueryRuleImpl> = [];
  private filterQueryGroups: Array<CubiqTS.QueryGroupImpl> = [];
  private blotterDefImpl: Array<CubiqTS.BlotterDefinitionImpl> = [];
  /**
   * Usage: in order to apply the copying od the filter with compatible attributes
   *  and not to delete the old ones
   */
  private setNewConditions: boolean;
  private refinedFilterConditions: Array<BfxBlotterFilterCondition> = [];
  private refinedFilterQueryGroupRules: Array<CubiqTS.QueryRuleImpl> = [];
  private refinedFilterQueryGroups: Array<CubiqTS.QueryGroupImpl> = [];

  /***************************************/
  /* DATA */
  public config: any = {
    debug: true,
  };

  public data = {
    dialogId: null,
    dialogEventSubject: new Subject<BfxDialogData>(),
    actionEvent: null,
    payload: null,
    dialogData: {
      show: false,
      width: 450,
      height: 150,
      title_text: "Copy Filter",
      message_text_invalid: "Copied Criteria Invalid",
      message_text_part_invalid: "Some Copied Criteria Invalid",
      message_text_name_invalid: "Filter Name Criteria Invalid",
      autocomplete_filterName: "",
      autocomplete_filterType: null,
      autocomplete_filterTypes: [],
      autocomplete_filterTypesFiltered: [],
    },
    blotterType: 0,
  };

  static containsEmptyCondition = (filterConditions: Array<BfxBlotterFilterCondition>): boolean =>
    filterConditions.length === 1 &&
    filterConditions[0].fieldName == null &&
    filterConditions[0].iconDisabled === false;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */
  constructor(
    private _translationService: TranslateService,
    private _filterService: BfxBlotterFilterService,
    private _element: ElementRef,
  ) {
    super();
  }

  ngAfterViewInit() {
    /** NOTE:  Call in case the drop down has not been open at all,
     *   because on confirm it still makes the call to save the filter.
     */
    setTimeout(() => {
      this.copyFilter_validateFilterType();
    }, 0);
    this.patchDialogInitDrag();
  }

  ngOnDestroy() {}

  private patchDialogInitDrag(): void {
    if (this._dialog["__bfx_initDrag_close_autocomplete"]) {
      return;
    }

    this._dialog["__bfx_initDrag_close_autocomplete"] = true;

    const oldFn = this._dialog.initDrag;

    this._dialog.initDrag = function () {
      this._copyFilter_filterType_autoComplete.hide();

      oldFn.call(this._dialog, arguments[0]);
    }.bind(this);
  }

  public openDialog(dialogData: BfxDialogData): Observable<any> {
    this.data.dialogId = dialogData.dialogId;

    let event: BfxBlotterFilterActionEvent = dialogData.event;
    this.data.actionEvent = event;
    this.data.dialogData.autocomplete_filterTypes = BFXBLOTTERFILTERTYPES.map((ft) => {
      return {
        label: ft.label,
        value: ft.value,
        ...(ft[EXTENDED_TYPE_PROPERTY_NAME] != null && {
          extendedTypeId: ft[EXTENDED_TYPE_PROPERTY_NAME],
        }),
      };
    });

    this.data.dialogData.autocomplete_filterTypes.forEach((type) => {
      if (
        type.value ===
          parseInt(this.data.actionEvent._selectedFilter.filterIdentifier.blotterType) &&
        (this.data.actionEvent._selectedFilter.extendedTypeId == null ||
          type.extendedTypeId === parseInt(this.data.actionEvent._selectedFilter.extendedTypeId))
      ) {
        this.data.dialogData.autocomplete_filterType = type;
        this.filterTypeValid = true;
      }
    });

    this.data.actionEvent._service.getAll().then((blotters: CubiqTS.BlotterDefinitionImpl[]) => {
      this.blotterDefImpl = blotters;
      this.data.dialogData.show = true;
    });

    this._filterService.setIsEditableMode(true);
    this._filterService.setIsDialogMode(true);

    NativeDesktopSupport.prepareEmbeddedDialog(dialogData.body, this._dialog, this._element);
    this.data.dialogData.show = true;

    return this.data.dialogEventSubject.asObservable();
  }

  public closeDialog(): void {
    super.closeDialog();
    this._filterService.setIsEditableMode(false);
    this._filterService.setIsDialogMode(false);
    this.data.dialogData.show = false;
    // inform dialog service that dialog is closed and to cleanup
    this.notifyDialogClosed(this.data.dialogEventSubject, this.data.dialogId);
  }

  /* COPY FILTER INTO- Actions */
  public actionCopyFilter_confirm(): void {
    if (this.filterTypeValid) {
      this.filterConditions = this.data.actionEvent._selectedFilter.conditions;
    }
    if (this.filterTypeValid || this.filterTypePartValid) {
      let blotterType: BlotterType = this.data.dialogData.autocomplete_filterType.value;
      let assetClass: AssetClass =
        this.data.actionEvent._selectedFilter.filterIdentifier.assetClass;

      // Call in case the drop down has not been open at all
      this.copyFilter_validateFilterType();

      let hasExtendedTypeProperty = this.data.dialogData.autocomplete_filterType.hasOwnProperty(
        EXTENDED_TYPE_PROPERTY_NAME,
      );
      let copyDestinationExtendedTypeId = hasExtendedTypeProperty
        ? this.data.dialogData.autocomplete_filterType[EXTENDED_TYPE_PROPERTY_NAME]
        : ExtendedOrderTypeIdentifier.OTHER;
      let copyOriginExtendedTypeId = this.data.actionEvent._selectedFilter.extendedTypeId;

      this._filterService
        .createFilter(
          FilterType.BlotterFilter,
          this.data.dialogData.autocomplete_filterName,
          blotterType,
          assetClass,
          true,
          copyDestinationExtendedTypeId,
        )
        .then((newFilter) => {
          // NOTE: needs to be a deep copy, otherwise it overwrites the selected filter queryGroup
          let selectedFilterQueryGroup = {
            ...this.data.actionEvent._selectedFilter.queryGroup,
          };
          let conditionsToBeSaved = this.setNewConditions
            ? this.refinedFilterConditions
            : this.filterConditions;
          let queryGroupsToBeSaved = this.setNewConditions
            ? this.refinedFilterQueryGroups
            : this.filterQueryGroups;
          let queryGroupRulesToBeSaved = this.setNewConditions
            ? this.refinedFilterQueryGroupRules
            : this.filterQueryGroupRules;

          newFilter.conditions = conditionsToBeSaved;
          newFilter.queryGroup = selectedFilterQueryGroup;
          if (blotterType === this.data.actionEvent._selectedFilter.filterIdentifier.blotterType) {
            newFilter.queryGroup.rules = this.setNewConditions
              ? this.refinedFilterQueryGroupRules
              : this.data.actionEvent._selectedFilter.queryGroup.rules;
          } else if (this.filterTypePartValid && newFilter.queryGroup.groups.length === 0) {
            newFilter.queryGroup.rules = queryGroupRulesToBeSaved;
          } else if (this.filterTypePartValid) {
            newFilter.queryGroup.groups = queryGroupsToBeSaved;
          } else {
            newFilter.queryGroup.rules = queryGroupRulesToBeSaved;
          }
          this._filterService
            .saveFilter(
              FilterType.BlotterFilter,
              newFilter,
              newFilter.queryGroup,
              true,
              copyOriginExtendedTypeId,
            )
            .then((response) => {
              this.closeDialog();
            })
            .catch((err) => {
              logger.out.error(
                this._translationService.instant("error.save.filter") +
                  "blotter type: " +
                  blotterType,
              );
            });
        })
        .catch((err) => {
          if (err.toString().includes("blotter.already.exists"))
            logger.out.error(
              this._translationService.instant(
                "filter.already.exists " +
                  "filter name: " +
                  [this.data.dialogData.autocomplete_filterName],
              ) +
                "blotter type: " +
                blotterType,
            );
          this.closeDialog();
        });
    }
  }

  public actionCopyFilter_close(): void {
    this.closeDialog();
  }

  public helperCopyFilter_searchAndValidateFilterType(event): void {
    let filteredFilterTypes = [];
    let filterTypes = this.data.dialogData.autocomplete_filterTypes;

    for (let i = 0; i < filterTypes.length; i++) {
      let filterType = filterTypes[i];
      if (
        event.query.length === 0 ||
        filterType.label.toLowerCase().indexOf(event.query.toLowerCase()) == 0
      ) {
        filteredFilterTypes.push(filterType);
      }
    }
    this.data.dialogData.autocomplete_filterTypesFiltered = filteredFilterTypes;
  }

  public helperCopyFilter_validateFilterName(event: any) {
    let data: string = event.query != null ? event.query : event;

    if (data.length == 0) {
      this.filterNameValid = false;
      this.filterNameEmpty = true;
      return;
    }
    if (data.length > 50) {
      this.filterNameValid = false;
      this.filterNameEmpty = false;
      this.data.dialogData.message_text_name_invalid =
        this._translationService.instant("filter.name.too.long");
      return;
    }
    if (this.filterNameExists(data)) {
      this.filterNameValid = false;
      this.filterNameEmpty = false;
      this.data.dialogData.message_text_name_invalid = this._translationService.instant(
        "blotter.already.exists",
        [data],
      );
      return;
    } else {
      this.filterNameValid = true;
      this.filterNameEmpty = false;
      this.data.dialogData.autocomplete_filterName = data;
    }
  }

  public copyFilter_validateFilterType(event = null): void {
    this.filterConditions = [];
    this.filterQueryGroupRules = [];
    let filter: any = { ...this.data.actionEvent._selectedFilter };
    let selectedData: BfxBlotterFilter = this.data.dialogData.autocomplete_filterType;

    if (
      parseInt(this.data.dialogData.autocomplete_filterType.value) ===
      this.data.actionEvent._selectedFilter.filterIdentifier.blotterType
    ) {
      /**  NOTE: because Benchmark blotter is kind of a subset of BlotterType.Order blotters,
       *        Below is isolating the case where conflicting filter attributes emerge
       *        also the fixingCode and OrderType attribute have to be purged from the list
       */
      if (filter.extendedTypeId != selectedData.extendedTypeId) {
        this.handleSameBlotterTypeDifferentExtendedId(filter, selectedData);
      } else {
        this.filterTypeValid = true;
        this.filterTypePartValid = this.filterTypeInvalid = false;
        this.filterConditions = filter.conditions;
        this.setNewConditions = false;
      }
    } else {
      if (!filter) return;
      let filterTypeValue = this.data.dialogData.autocomplete_filterType.value;
      let filterExtendedTypeId = this.data.dialogData.autocomplete_filterType.extendedTypeId;
      let tableDef: any = this.getTableDefFromMap(filterTypeValue, filterExtendedTypeId);
      if (!tableDef) {
        this.filterTypeValid = false;
        this.filterTypeInvalid = true;
        return;
      }
      let allAdAndOr: boolean = true;

      this.setNewConditions = false;
      filter.conditions.forEach((condition) => {
        if (!condition.add && !condition.or) {
          if (
            this.matchCondition(tableDef.columns, condition, "fieldName", this.filterConditions)
          ) {
            allAdAndOr = false;
          }
        } else this.filterConditions.push(condition);
      });
      if (
        this.filterConditions.length > 0 &&
        this.filterConditions.length == filter.conditions.length
      ) {
        this.filterTypeValid = true;
        this.filterTypeInvalid = this.filterTypePartValid = false;
      } else if (
        this.filterConditions.length > 0 &&
        this.filterConditions.length != filter.conditions.length
      ) {
        if (!allAdAndOr) {
          this.filterTypeValid = this.filterTypeInvalid = false;
          this.filterTypePartValid = true;
        } else {
          this.filterTypeValid = this.filterTypePartValid = false;
          this.filterTypeInvalid = true;
        }
      } else if (this.filterConditions.length == 0) {
        this.filterTypeValid = this.filterTypePartValid = false;
        this.filterTypeInvalid = true;
      }
      if (this.filterTypeValid || this.filterTypePartValid) {
        if (filter.queryGroup.groups.length == 0) {
          filter.queryGroup.rules.forEach((rule) => {
            this.matchCondition(tableDef.columns, rule, "field", this.filterQueryGroupRules);
          });
        } else {
          filter.queryGroup.groups.forEach((group) => {
            let rules: Array<CubiqTS.QueryRuleImpl> = [];
            group.rules.forEach((rule) => {
              this.matchCondition(tableDef.columns, rule, "field", rules);
            });
            group.rules = rules;
            this.filterQueryGroups.push(group);
          });
        }
      }
    }
    this.helperCopyFilter_validateFilterName({
      query: this.data.dialogData.autocomplete_filterName,
    });
  }

  private matchCondition(
    columns: Array<any>,
    item: any,
    itemPropertyNm: string,
    addToArr: Array<any>,
    fieldsToOmit?: Array<string>,
  ): boolean {
    if (
      columns.find((col) => {
        let match: boolean = false;
        if (col.field == item[itemPropertyNm]) {
          match = true;
        }
        if (!match && col.alias) {
          col.alias.forEach((alias) => {
            if (alias && alias == item[itemPropertyNm]) {
              // when creating the filter we want to use the propery on the column for this grid
              item = Object.assign({}, item);
              item[itemPropertyNm] = col.field;
              match = true;
            }
          });
        }
        return match;
      })
    ) {
      let addToArray = fieldsToOmit != null ? !fieldsToOmit.includes(item[itemPropertyNm]) : true;
      if (addToArray) {
        addToArr.push(item);
      }
      return true;
    }
    return false;
  }

  private getTableDefFromMap(type: any, extendedTypeId: any): any {
    if (type === null || type === undefined) return;
    let definitionsMap: any = this.data.actionEvent._definitionsMap;
    let definitionsMapSearchKey = type + "-" + 0 + "-" + extendedTypeId;
    switch (type) {
      case 0:
        this.data.blotterType = CubiqTS.BlotterType.ORDER;
        return definitionsMap[definitionsMapSearchKey];
      case 1:
        this.data.blotterType = CubiqTS.BlotterType.TRADE;
        return definitionsMap[definitionsMapSearchKey];
      case 3:
        this.data.blotterType = CubiqTS.BlotterType.INTERVENTION;
        return definitionsMap[definitionsMapSearchKey];
    }
  }
  private filterNameExists(event): boolean {
    let selectedBlotterType = this.data.dialogData.autocomplete_filterType.value;
    let data: string = event.query != null ? event.query : event;
    return (
      this.blotterDefImpl.filter(
        (f) =>
          f.blotterType == selectedBlotterType &&
          this.isolateBasedOnExtendedTypeId(f) &&
          f[this.data.actionEvent._field].toLowerCase() == data.toLowerCase(),
      ).length > 0
    );
  }

  protected handleSameBlotterTypeDifferentExtendedId(filter: any, selectedData: BfxBlotterFilter) {
    this.refinedFilterQueryGroups = [];
    this.refinedFilterQueryGroupRules = [];
    let conflictingDirection =
      filter.extendedTypeId !== ExtendedOrderTypeIdentifier.BENCHMARK &&
      selectedData.extendedTypeId !== ExtendedOrderTypeIdentifier.OTHER;

    this.setNewConditions = true;
    this.refinedFilterConditions = this.omitConditionsFromFilter(
      filter.conditions,
      CONFLICTING_ORDER_DATA_FIELDS_FOR_FILTER_CONDITIONS,
    );

    this.filterTypeValid = conflictingDirection
      ? !this.filterContainsConflictingFilterValues(this.refinedFilterConditions, "product", [
          "NDF",
        ])
      : true;

    if (
      this.refinedFilterConditions.length === 0 ||
      BfxDialogFilterCopyComponent.containsEmptyCondition(this.refinedFilterConditions) ||
      !this.filterTypeValid
    ) {
      this.filterTypeValid = this.filterTypePartValid = false;
      this.filterTypeInvalid = true;
    } else {
      this.filterTypePartValid = this.filterTypeInvalid = false;
    }
    this.filterConditions = filter.conditions;

    /* warn user if there are conflicting conditions */
    if (this.filterTypeValid) {
      this.filterTypePartValid =
        this.refinedFilterConditions.length !== this.filterConditions.length;
      this.filterTypeValid = !this.filterTypePartValid;
    }

    let filterTypeValue = this.data.dialogData.autocomplete_filterType.value;
    let filterExtendedTypeId: ExtendedOrderTypeIdentifier =
      this.data.dialogData.autocomplete_filterType.extendedTypeId;
    let tableDef: any = this.getTableDefFromMap(filterTypeValue, filterExtendedTypeId);
    let filterQueryGroup: QueryGroup = { ...filter.queryGroup };
    if (this.filterTypeValid || this.filterTypePartValid) {
      if (filterQueryGroup.groups.length == 0) {
        filterQueryGroup.rules.forEach((rule) => {
          this.matchCondition(
            tableDef.columns,
            rule,
            "field",
            this.refinedFilterQueryGroupRules,
            CONFLICTING_ORDER_DATA_FIELDS_FOR_FILTER_CONDITIONS,
          );
        });
      } else {
        filterQueryGroup.groups.forEach((group) => {
          let rules: Array<CubiqTS.QueryRuleImpl> = [];
          group.rules.forEach((rule) => {
            this.matchCondition(
              tableDef.columns,
              rule,
              "field",
              rules,
              CONFLICTING_ORDER_DATA_FIELDS_FOR_FILTER_CONDITIONS,
            );
          });
          group.rules = rules;
          this.refinedFilterQueryGroups.push(group);
        });
      }
    }
  }

  protected omitConditionsFromFilter(
    conditionsList: Array<BfxBlotterFilterCondition>,
    conditionNames: Array<string>,
  ): Array<BfxBlotterFilterCondition> {
    return conditionsList.filter(
      (condition: BfxBlotterFilterCondition) => !conditionNames.includes(condition.fieldName),
    );
  }

  protected filterContainsConflictingFilterValues(
    filterConditions: Array<BfxBlotterFilterCondition>,
    conflictingAttribute: string,
    conflictingValues: Array<string>,
  ): boolean {
    let containsConflictingValue = false;
    filterConditions.forEach((condition: BfxBlotterFilterCondition) => {
      if (condition.fieldName === conflictingAttribute) {
        containsConflictingValue = condition.value.some((value) =>
          conflictingValues.includes(value.value),
        );
      }
    });
    return containsConflictingValue;
  }

  protected isolateBasedOnExtendedTypeId(item: CubiqTS.BlotterDefinitionImpl): boolean {
    let selectedFilterDestination = this.data.dialogData.autocomplete_filterType;
    let isBmarkFilter =
      selectedFilterDestination.extendedTypeId === ExtendedOrderTypeIdentifier.BENCHMARK;
    if (selectedFilterDestination.value === CubiqTS.BlotterType.ORDER) {
      let itemExtendedType = item.extendedInfo.get(EXTENDED_TYPE_PROPERTY_NAME);
      return itemExtendedType != null
        ? ExtendedOrderTypeIdentifier[itemExtendedType] ==
            ExtendedOrderTypeIdentifier[selectedFilterDestination.extendedTypeId]
        : !isBmarkFilter;
    }
    return true;
  }
}
